import { reactive } from "vue";
import services from "@/composable/services";
import { useI18n } from "vue-i18n";
import {
  type CurrentUserView,
  type ManagementRole,
  type RoleChangeBuckets,
  type UserRow,
  applicationManagerTooltipKey,
  createEmptyBuckets,
  isApplicationManagerDisabled,
  isSelfRevokeBlocked,
  isUserManagerDisabled,
  MANAGEMENT_ROLES,
  trackChange,
  userManagerTooltipKey,
} from "./roleManagementChangesCore";

/**
 * Composable encapsulant la logique de l'ecran "Gestion des droits de
 * gestion pour l'application" : suivi des cochages / decochages de
 * {@code applicationManager} et {@code userManager} , persistance vers
 * le backend , et regles d'activation des checkboxes ( anti
 * auto-revocation , heritage SQL applicationManager -> userManager ) .
 *
 * <p><b>Pourquoi un composable</b> : la vue accumulait ~150 lignes de
 * logique metier dans son {@code setup} ( buckets , cascade de roles ,
 * garde-fous serveur , i18n des tooltips ) , difficile a tester en
 * isolation . L'extraction expose une API testable et isole les
 * dependances Vue / Vue-i18n / services dans une fine couche
 * d'adaptation .
 *
 * <p>Les fonctions purement deterministes ( {@link trackChange} ,
 * {@link isSelfRevokeBlocked} , ...) vivent dans
 * {@code roleManagementChangesCore} et sont testables sans Vue
 * context .
 */
interface UseRoleManagementChangesOptions {
  applicationName: string;
  currentUser: CurrentUserView;
}

export function useRoleManagementChanges(opts: UseRoleManagementChangesOptions) {
  const { t } = useI18n();
  const changes = reactive<RoleChangeBuckets>(createEmptyBuckets());

  function resetChanges(): void {
    Object.assign(changes, createEmptyBuckets());
  }

  /**
   * Handler de toggle a brancher sur les checkboxes . Encode la
   * cascade descendante {@code applicationManager} -&gt;
   * {@code userManager} : la mutation locale {@code row.isUserManager}
   * suit l'etat de la case parente pour rester WYSIWYG .
   */
  function onToggle(value: boolean, row: UserRow, role: ManagementRole): void {
    if (!row?.id) return;
    trackChange(changes, role, value, row.id);
    if (role === "applicationManager") {
      trackChange(changes, "userManager", value, row.id);
      row.isUserManager = value;
    }
  }

  function applicationManagerTooltip(row: UserRow): string {
    const key = applicationManagerTooltipKey(row, opts.currentUser);
    return key ? t(key) : "";
  }

  function userManagerTooltip(row: UserRow): string {
    const key = userManagerTooltipKey(row, opts.currentUser);
    return key ? t(key) : "";
  }

  /**
   * Persiste les buckets via le service d'autorisation . Utilise
   * {@code Promise.allSettled} pour qu'un refus serveur ( ex :
   * auto-revocation HTTP 400 ) n'annule pas les autres operations .
   * Les rejets sont remontes via le service d'alerte .
   *
   * @returns nombre d'operations reussies / echouees ( utile pour les
   *          tests et un eventuel statut global UI ) .
   */
  async function persistChanges(): Promise<{ succeeded: number; failed: number }> {
    const tasks: Promise<unknown>[] = [];
    MANAGEMENT_ROLES.forEach((role) => {
      changes[role].add.forEach(userId => tasks.push(grant(role, userId)));
      changes[role].remove.forEach(userId => tasks.push(revoke(role, userId)));
    });
    const results = await Promise.allSettled(tasks);
    const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
    failed.forEach(r => services.alertService.toastServerError(r.reason));
    return { succeeded: results.length - failed.length, failed: failed.length };
  }

  function grant(role: ManagementRole, userId: string) {
    return services.authorizationService.createAuthorizedRole(role, userId, undefined, opts.applicationName);
  }

  function revoke(role: ManagementRole, userId: string) {
    return services.authorizationService.revokeAuthorizedRole(role, userId, undefined, opts.applicationName);
  }

  return {
    changes,
    resetChanges,
    onToggle,
    persistChanges,
    isSelfRevokeBlocked: (row: UserRow) => isSelfRevokeBlocked(row, opts.currentUser),
    isApplicationManagerDisabled: (row: UserRow) => isApplicationManagerDisabled(row, opts.currentUser),
    isUserManagerDisabled: (row: UserRow) => isUserManagerDisabled(row, opts.currentUser),
    applicationManagerTooltip,
    userManagerTooltip,
  };
}
